import { useEffect } from "react";
import { useLocation, useMatches } from "react-router-dom";
import {
  DEFAULT_IMAGE,
  DEFAULT_IMAGE_ALT,
  SEO_PAGES,
  SITE_NAME,
  buildJsonLd,
  normalizePath,
  pageUrl,
} from "@src/constant/seo";

const JSON_LD_ID = "primus-jsonld";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertJsonLd(data) {
  let element = document.getElementById(JSON_LD_ID);
  if (!element) {
    element = document.createElement("script");
    element.id = JSON_LD_ID;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function removeMeta(selector) {
  document.head.querySelector(selector)?.remove();
}

/**
 * Keeps document title, social meta, canonical URL, and JSON-LD in sync with the route.
 */
export default function Seo() {
  const { pathname } = useLocation();
  const isNotFound = useMatches().some((match) => match.handle?.notFound);
  const path = normalizePath(pathname);
  const page = SEO_PAGES[path];

  useEffect(() => {
    if (isNotFound) {
      const url = pageUrl(path);
      document.title = `Page not found | ${SITE_NAME}`;
      upsertMeta('meta[name="description"]', {
        name: "description",
        content: "This page could not be found on Primus.",
      });
      upsertMeta('meta[name="robots"]', {
        name: "robots",
        content: "noindex, follow",
      });
      upsertLink("canonical", url);
      upsertMeta('meta[property="og:url"]', {
        property: "og:url",
        content: url,
      });
      upsertMeta('meta[property="og:title"]', {
        property: "og:title",
        content: `Page not found | ${SITE_NAME}`,
      });
      return;
    }

    if (!page) {
      const url = pageUrl(path);
      document.title = SITE_NAME;
      removeMeta('meta[name="description"]');
      upsertMeta('meta[name="robots"]', {
        name: "robots",
        content: "index, follow",
      });
      upsertLink("canonical", url);
      upsertMeta('meta[property="og:url"]', {
        property: "og:url",
        content: url,
      });
      upsertMeta('meta[property="og:title"]', {
        property: "og:title",
        content: SITE_NAME,
      });
      removeMeta('meta[property="og:description"]');
      removeMeta('meta[name="twitter:description"]');
      return;
    }

    const url = pageUrl(path);
    document.title = page.title;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: page.description,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: "index, follow",
    });
    upsertLink("canonical", url);

    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE_NAME,
    });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: "en_US",
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: page.title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: page.description,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: DEFAULT_IMAGE,
    });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: DEFAULT_IMAGE_ALT,
    });
    upsertMeta('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    });
    upsertMeta('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: page.title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: page.description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: DEFAULT_IMAGE,
    });
    upsertMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: DEFAULT_IMAGE_ALT,
    });

    upsertJsonLd(buildJsonLd(path, page));
  }, [isNotFound, page, path]);

  return null;
}
